import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { LockIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/button";
import { FieldError, Label, TextField } from "@/components/field";

import { directPurchaseApi } from "../../onboarding/api/directPurchase.api";
import { EmailInput } from "../../onboarding/components/EmailInput";
import {
  createCheckoutLead,
  updateCheckoutLeadEmail,
} from "../api/checkoutLeads.api";
import { checkoutPrimaryButtonClassName } from "./checkoutStyles";

const checkoutEmailSchema = z.object({
  email: z
    .string()
    .trim()
    .email("Enter a valid email so we can save your progress."),
});

type CheckoutEmailFormData = z.infer<typeof checkoutEmailSchema>;

const EXISTING_ACCOUNT_MESSAGE = "This email already exists in our platform.";

const normalizeEmail = (email?: null | string) =>
  (email ?? "").trim().toLowerCase();

interface CheckoutEmailStepProps {
  checkoutLeadId: null | string;
  couponCode?: string;
  defaultEmail: string;
  onContinue: (result: { email: string; id: null | string }) => void;
  // The app links its own signed in users here with their email (upgrades, reactivations), so only other existing emails are blocked.
  ownAccountEmail?: string;
  productId?: null | string;
  stripePriceId?: null | string;
}

export function CheckoutEmailStep({
  checkoutLeadId,
  couponCode,
  defaultEmail,
  onContinue,
  ownAccountEmail,
  productId,
  stripePriceId,
}: CheckoutEmailStepProps) {
  const { control, handleSubmit, watch } = useForm<CheckoutEmailFormData>({
    defaultValues: { email: defaultEmail },
    mode: "onBlur",
    resolver: zodResolver(checkoutEmailSchema),
  });
  const typedEmail = normalizeEmail(watch("email"));
  const [blockedEmail, setBlockedEmail] = useState<null | string>(null);
  const isBlocked = !!typedEmail && typedEmail === blockedEmail;

  const isSavingRef = useRef(false);
  const saveEmail = useMutation({
    mutationFn: async ({ email }: CheckoutEmailFormData) => {
      if (normalizeEmail(email) !== normalizeEmail(ownAccountEmail)) {
        const { exists } = await directPurchaseApi.checkEmail(email);

        if (exists) return null;
      }

      // Lead capture is tracking only, so a failure here must not block the payment step.
      try {
        if (checkoutLeadId) {
          await updateCheckoutLeadEmail(checkoutLeadId, email);

          return { email, id: checkoutLeadId };
        }

        const { id } = await createCheckoutLead({
          couponCode: couponCode || undefined,
          email,
          productId: productId || undefined,
          stripePriceId: stripePriceId || undefined,
        });

        return { email, id };
      } catch (error) {
        console.error("Failed to save checkout lead:", error);

        return { email, id: checkoutLeadId };
      }
    },
    onSettled: () => {
      isSavingRef.current = false;
    },
    onSuccess: (result, { email }) => {
      if (!result) {
        setBlockedEmail(normalizeEmail(email));

        return;
      }

      onContinue(result);
    },
  });

  return (
    <form
      className="flex flex-col gap-6"
      noValidate
      onSubmit={handleSubmit((data) => {
        if (isSavingRef.current) return;
        isSavingRef.current = true;
        saveEmail.mutate(data);
      })}
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-display-sm font-semibold text-text-primary">
          Save your spot!
        </h1>
        <p className="text-md text-text-tertiary">
          Add your email and we&apos;ll hold the price while you finish
          checkout.
        </p>
      </div>

      <Controller
        control={control}
        name="email"
        render={({
          field: { name, onBlur, onChange, ref, value },
          fieldState: { error, invalid },
        }) => (
          <TextField
            isInvalid={invalid || isBlocked}
            isRequired
            name={name}
            onBlur={onBlur}
            validationBehavior="aria"
          >
            <Label>Work email</Label>
            <EmailInput
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              onClear={() => onChange("")}
              placeholder="you@company.com"
              ref={ref}
              showClearButton={!!value}
              value={value}
            />
            <FieldError>
              {isBlocked ? EXISTING_ACCOUNT_MESSAGE : error?.message}
            </FieldError>
          </TextField>
        )}
      />

      <div className="flex flex-col gap-2">
        <Button
          className={checkoutPrimaryButtonClassName(saveEmail.isPending)}
          isDisabled={!typedEmail || isBlocked || saveEmail.isPending}
          isLoading={saveEmail.isPending}
          size="lg"
          type="submit"
        >
          Continue to payment
        </Button>
        {saveEmail.isError && (
          <p className="text-sm text-text-error-primary" role="alert">
            We couldn&apos;t check your email. Please try again.
          </p>
        )}
      </div>

      <p className="flex items-start gap-2 text-xs text-text-tertiary">
        <LockIcon aria-hidden className="size-4 shrink-0" />
        Your email stays private and is only used for your account and order
        updates.
      </p>
    </form>
  );
}
